import React, { useEffect, useState } from "react";
import { Box, Spinner, Heading, useTheme } from "@chakra-ui/react";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const Chart2 = ({ supplier, retailer }) => {
  const theme = useTheme();
  const [chartDiv, setChartDiv] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let root;

    const initChart = async () => {
      if (chartDiv) {
        setLoading(true);
        try {
          const res = await axios.post(
            `${process.env.REACT_APP_API_URL}/rep_sales/chart2`,
            {
              supplier,
              retailer,
              userid: localStorage.getItem("userid"),
            }
          );

          const data = res.data.data.map((item) => ({
            region: item.region,
            value: Math.round(item.value),
          }));

          root = am5.Root.new(chartDiv);
          root.setThemes([am5themes_Animated.new(root)]);

          const chart = root.container.children.push(
            am5xy.XYChart.new(root, {
              layout: root.verticalLayout,
              panX: false,
              panY: false,
              wheelX: "none",
              wheelY: "none",
              paddingLeft: 15,
              paddingBottom: 30,
              paddingRight: 25,
            })
          );

          // Y axis (categories)
          const yAxis = chart.yAxes.push(
            am5xy.CategoryAxis.new(root, {
              categoryField: "region",
              renderer: am5xy.AxisRendererY.new(root, {
                inversed: true,
                cellStartLocation: 0.1,
                cellEndLocation: 0.9,
                minGridDistance: 20,
              }),
            })
          );

          yAxis.data.setAll(data);

          yAxis.get("renderer").labels.template.setAll({
            fontSize: 11,
            maxWidth: 140,
            oversizedBehavior: "wrap",
            textAlign: "end",
            fill: am5.color("#9ca3af"),
          });

          yAxis.get("renderer").grid.template.setAll({
            visible: false,
          });

          // X axis (values)
          const xAxis = chart.xAxes.push(
            am5xy.ValueAxis.new(root, {
              renderer: am5xy.AxisRendererX.new(root, {
                strokeOpacity: 0,
              }),
            })
          );

          xAxis.get("renderer").labels.template.setAll({
            fontSize: 11,
            fill: am5.color("#9ca3af"),
          });

          xAxis.get("renderer").grid.template.setAll({
            strokeOpacity: 0.15,
            strokeDasharray: [3, 3],
            stroke: am5.color("#e5e7eb"),
          });

          xAxis.get("renderer").set("minGridDistance", 70);

          // Series
          const series = chart.series.push(
            am5xy.ColumnSeries.new(root, {
              name: "Value",
              xAxis: xAxis,
              yAxis: yAxis,
              valueXField: "value",
              categoryYField: "region",
              sequencedInterpolation: true,
              fill: am5.color("#58b6c0"),
            })
          );

          series.data.setAll(data);

          const tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            labelText: "{categoryY}: R{valueX}",
          });
          tooltip.get("background").setAll({
            fill: am5.color("#ffffff"),
            fillOpacity: 0.95,
            stroke: am5.color("#e5e7eb"),
            strokeWidth: 1,
            shadowColor: am5.color("#000000"),
            shadowBlur: 8,
            shadowOffsetX: 0,
            shadowOffsetY: 2,
            shadowOpacity: 0.08,
            cornerRadius: 6,
          });
          tooltip.label.setAll({
            fill: am5.color("#374151"),
            fontSize: 12,
          });

          series.columns.template.setAll({
            height: am5.percent(100),
            strokeOpacity: 0,
            fillOpacity: 0.8,
            cornerRadiusTR: 4,
            cornerRadiusBR: 4,
            tooltip: tooltip,
            tooltipText: "{categoryY}: R{valueX}",
          });

          series.columns.template.adapters.add(
            "tooltipText",
            function (text, target) {
              const dataItem = target.dataItem;
              if (dataItem) {
                const category = dataItem.get("categoryY");
                const value = dataItem.get("valueX");
                const formattedValue = root.numberFormatter.format(value, "#,###");
                return `${category}: R${formattedValue}`;
              }
              return text;
            }
          );

          // Value labels on bars
          series.bullets.push(function () {
            const label = am5.Label.new(root, {
              text: "",
              populateText: true,
              centerY: am5.p50,
              paddingLeft: 5,
              paddingRight: 5,
              paddingTop: 2,
              paddingBottom: 2,
              fontSize: 9,
            });

            label.adapters.add("text", function (text, target) {
              const dataItem = target.dataItem;
              if (dataItem) {
                const value = dataItem.get("valueX");
                if (value >= 1000000) return `R${(value / 1000000).toFixed(1)}M`;
                if (value >= 1000) return `R${(value / 1000).toFixed(0)}K`;
                return `R${root.numberFormatter.format(value, "#,###")}`;
              }
              return text;
            });

            label.adapters.add("centerX", function (centerX, target) {
              const dataItem = target.dataItem;
              if (dataItem) {
                const series = dataItem.component;
                const xAxis = series.get("xAxis");
                const value = dataItem.get("valueX");
                const max = xAxis.getPrivate("max");
                const percentage = value / max;
                return percentage < 0.7 ? am5.p0 : am5.p100;
              }
              return centerX;
            });

            label.adapters.add("dx", function (dx, target) {
              const dataItem = target.dataItem;
              if (dataItem) {
                const series = dataItem.component;
                const xAxis = series.get("xAxis");
                const value = dataItem.get("valueX");
                const max = xAxis.getPrivate("max");
                const percentage = value / max;
                return percentage < 0.6 ? 20 : 0;
              }
              return dx;
            });

            label.adapters.add("fill", function (fill, target) {
              const dataItem = target.dataItem;
              if (dataItem) {
                const series = dataItem.component;
                const xAxis = series.get("xAxis");
                const value = dataItem.get("valueX");
                const max = xAxis.getPrivate("max");
                const percentage = value / max;
                return percentage < 0.7
                  ? am5.color("#6b7280")
                  : am5.color("#ffffff");
              }
              return fill;
            });

            return am5.Bullet.new(root, {
              locationX: 1,
              locationY: 0.5,
              sprite: label,
            });
          });

          chart.appear(1000, 100);

          setLoading(false);
        } catch (error) {
          console.error("Error fetching data", error);
          setLoading(false);
        }
      }
    };

    initChart();

    return () => {
      if (root) {
        root.dispose();
      }
    };
  }, [chartDiv, supplier, retailer]);

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      position="relative"
    >
      <Heading size="xs" color="gray.600" mb={2} fontWeight="600">
        Sales by Region
      </Heading>
      <Box flex="1" position="relative">
        <div
          ref={(div) => setChartDiv(div)}
          style={{ width: "100%", height: "100%", overflow: "hidden" }}
        ></div>
        {loading && (
          <Spinner
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
          />
        )}
      </Box>
    </Box>
  );
};

export default Chart2;
